import React, { useState, useEffect } from "react";
import proposeTalent from "./../../images/propose-talent.svg";
import { useNavigate } from "react-router-dom";
import Select, { components } from "react-select";
import { customStyles } from "./../../helpers/StyleUtils";
import "../../App.css";
import { userService, bookingService } from "../../api/apiService";
import { useAuth } from '../../contexts/AuthContext';
import "../../styles/NewCalendar.css";
import { getOrderType, isTypedBooking, orderTypeLabel } from '../../helpers/orderType';

const NewOrdersList = ({ events, onEventClick, initialWeek }) => {
  // Log the first event to see its structure
  useEffect(() => {
    if (events && events.length > 0) {
      // console.log('Events in NewOrdersList:', events[0]);
    }
  }, [events]);

  const { currentUser, updateUserPreferences } = useAuth();
  
  let userEmail = localStorage.getItem("userEmail");
  let userId = localStorage.getItem("userId");
  const [filterPreferences, setFilterPreferences] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCreatedAt, setSelectedCreatedAt] = useState(null);
  const [selectedstartDate, setSelectedstartDate] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedSet, setSelectedSet] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedPreferenceIndex, setSelectedPreferenceIndex] = useState(null);
  const [selectedOrders, setSelectedOrders] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentWeek, setCurrentWeek] = useState(initialWeek || getCurrentWeek());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [userEmailCache, setUserEmailCache] = useState({});
  const processedGroups = new Set();

  // Helper function to get current week number
  function getCurrentWeek() {
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 1);
    const diff = now - start;
    const oneWeek = 7 * 24 * 60 * 60 * 1000;
    return Math.ceil(diff / oneWeek);
  }

  // Helper function to get ISO week number
  function getISOWeek(date) {
    const target = new Date(date.valueOf());
    const dayNr = (date.getDay() + 6) % 7;
    target.setDate(target.getDate() - dayNr + 3);
    const firstThursday = target.valueOf();
    target.setMonth(0, 1);
    if (target.getDay() !== 4) {
      target.setMonth(0, 1 + ((4 - target.getDay()) + 7) % 7);
    }
    return 1 + Math.ceil((firstThursday - target) / 604800000);
  }

  // Helper function to get dates for a specific week and year
  function getWeekDates(weekNumber, year) {
    // Get January 4th of the year (this is always in week 1 according to ISO 8601)
    const jan4 = new Date(year, 0, 4);
    
    // Find the Monday of week 1
    const mondayOfWeek1 = new Date(jan4);
    mondayOfWeek1.setDate(jan4.getDate() - ((jan4.getDay() + 6) % 7));
    
    // Calculate the Monday of the specified week (start of day)
    const weekStart = new Date(mondayOfWeek1);
    weekStart.setDate(mondayOfWeek1.getDate() + (weekNumber - 1) * 7);
    weekStart.setHours(0, 0, 0, 0);

    // Calculate the Sunday of the specified week (END of day, so Saturday AND Sunday
    // bookings are inside the range — booking dates carry a time component)
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);

    return { weekStart, weekEnd, year: weekStart.getFullYear() };
  }

  // Navigation functions
  const goToPreviousWeek = () => {
    if (currentWeek === 1) {
      // Go to last week of previous year
      setCurrentYear(currentYear - 1);
      setCurrentWeek(52); // Most years have 52 weeks
    } else {
      setCurrentWeek(currentWeek - 1);
    }
  };

  const goToNextWeek = () => {
    const { weekStart } = getWeekDates(currentWeek + 1, currentYear);
    const weekYear = weekStart.getFullYear();
    
    if (weekYear > currentYear) {
      // Moving to next year
      setCurrentYear(weekYear);
      setCurrentWeek(1);
    } else if (currentWeek >= 52) {
      // Check if year has 53 weeks
      const dec31 = new Date(currentYear, 11, 31);
      const lastWeek = getISOWeek(dec31);
      if (currentWeek < lastWeek) {
        setCurrentWeek(currentWeek + 1);
      } else {
        // Go to first week of next year
        setCurrentYear(currentYear + 1);
        setCurrentWeek(1);
      }
    } else {
      setCurrentWeek(currentWeek + 1);
    }
  };

  const goToCurrentWeek = () => {
    const now = new Date();
    setCurrentWeek(getISOWeek(now));
    setCurrentYear(now.getFullYear());
  };

  // Function to get user email by user ID
  const getUserEmail = async (userId) => {
    if (!userId) return '';
    
    // If it's already an email address, return it directly
    if (userId.includes('@')) {
      return userId;
    }
    
    // If it's the current user, return their email directly
    if (userId === localStorage.getItem('userId')) {
      return userEmail;
    }
    
    // Check cache first
    if (userEmailCache[userId]) {
      return userEmailCache[userId];
    }
    
    try {
      const response = await userService.getUserById(userId);
      const email = response.data?.email || response.data?.userEmail || 'Unknown User';
      
      // Cache the result
      setUserEmailCache(prev => ({
        ...prev,
        [userId]: email
      }));
      
      return email;
    } catch (error) {
      console.error(`Error fetching user email for ID ${userId}:`, error);
     // return `User ID: ${userId}`;
    }
  };

  useEffect(() => {
    const fetchPreferences = async () => {
      if (!userEmail) {
        // console.log('No userEmail found, skipping preference fetch');
        return;
      }

      try {
        setLoading(true);
        // console.log('Fetching user preferences for:', userEmail);
        
        // First try to get from localStorage
        const localPrefs = localStorage.getItem(`filterPreferences_${userEmail}`);
        if (localPrefs) {
          try {
            const parsedPrefs = JSON.parse(localPrefs);
            // console.log('Found preferences in localStorage:', parsedPrefs);
            setFilterPreferences(parsedPrefs);
          } catch (e) {
            console.error('Error parsing local preferences:', e);
          }
        }
        
        // Then try to get from API (which will override localStorage if successful)
        const response = await userService.getProfile();
        // console.log('User profile response:', response);
        
        if (response.data && response.data.filterPreferences) {
          setFilterPreferences(response.data.filterPreferences);
          // console.log('Filter preferences loaded from API:', response.data.filterPreferences);
          
          // Update localStorage with the server version
          localStorage.setItem(`filterPreferences_${userEmail}`, JSON.stringify(response.data.filterPreferences));
        } else {
          // console.log("No preferences found in user profile");
          // If we didn't already load from localStorage and nothing from API, initialize empty
          if (!localPrefs) {
            setFilterPreferences([]);
          }
        }
      } catch (error) {
        console.error('Error fetching preferences from API:', error);
        // console.log("Using localStorage preferences or initializing with empty array");
        
        // If we haven't already loaded from localStorage, initialize empty
        if (!localStorage.getItem(`filterPreferences_${userEmail}`)) {
          setFilterPreferences([]);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPreferences();
  }, [userEmail]);

  const applyFilterPreference = (index) => {
    if (index < 0 || index >= filterPreferences.length) {
      return;
    }

    const preference = filterPreferences[index];
    setSearchTerm(preference.searchTerm || "");
    setSelectedCreatedAt(preference.selectedCreatedAt || null);
    setSelectedClient(preference.selectedClient || null);
    setSelectedPosition(preference.selectedPosition || null);
    setSelectedSet(preference.selectedSet || null);
    // Handle both single location (old format) and multi location (new format)
    if (preference.selectedLocation) {
      if (Array.isArray(preference.selectedLocation)) {
        setSelectedLocation(preference.selectedLocation);
      } else {
        // Convert single location to array format for backward compatibility
        setSelectedLocation([preference.selectedLocation]);
      }
    } else {
      setSelectedLocation(null);
    }
    setSelectedUser(preference.selectedUser || null);
  };

  const preferenceOptions = filterPreferences.map((_, index) => ({
    value: index,
    label: `Preference ${index + 1}`,
  }));
  
  const handlePreferenceChange = (selectedOption) => {
    setSelectedPreferenceIndex(selectedOption ? selectedOption.value : null);
    if (selectedOption) {
      applyFilterPreference(selectedOption.value);
    } else {
      setSearchTerm("");
      setSelectedCreatedAt(null);
      setSelectedClient(null);
      setSelectedPosition(null);
      setSelectedSet(null);
      setSelectedLocation(null);
      setSelectedUser(null);
    }
  };

  const handleCheckboxChange = (index) => {
    setSelectedOrders((prevSelected) => ({
      ...prevSelected,
      [index]: !prevSelected[index],
    }));
  };
  
  const saveOrUpdateFilterPreference = async () => {
    const newPreference = {
      searchTerm,
      selectedCreatedAt,
      selectedClient,
      selectedPosition,
      selectedSet,
      selectedLocation,
      selectedUser,
    };

    let updatedPreferences = [...filterPreferences];
    
    if (selectedPreferenceIndex !== null) {
      // Update an existing preference
      updatedPreferences[selectedPreferenceIndex] = newPreference;
    } else {
      // Save a new preference if there's room
      if (updatedPreferences.length < 3) {
        updatedPreferences.push(newPreference);
      } else {
        alert('You can only save up to 3 filter preferences.');
        return;
      }
    }

    try {
      setLoading(true);
      // console.log('Saving filter preferences for user:', userEmail);
      // console.log('Preferences to save:', updatedPreferences);
      
      // Save to localStorage first (will be available even if API fails)
      localStorage.setItem(`filterPreferences_${userEmail}`, JSON.stringify(updatedPreferences));
      
      // Update user profile with new preferences using Auth context
      await updateUserPreferences({
        filterPreferences: updatedPreferences
      });
      
      setFilterPreferences(updatedPreferences); // Update local state
      alert('Filter preferences updated successfully.');
    } catch (error) {
      console.error('Error updating preferences on server:', error);
      // Still update local state since we saved to localStorage
      setFilterPreferences(updatedPreferences);
      alert('Preferences saved locally, but server update failed. Your preferences will be available on this device.');
    } finally {
      setLoading(false);
    }
  };

  const markTheOrder = async () => {
    const selectedOrderIds = Object.keys(selectedOrders).filter(
      (id) => selectedOrders[id]
    );

    if (selectedOrderIds.length === 0) {
      return;
    }

    try {
      setLoading(true);
      const updatePromises = selectedOrderIds.map(id => 
        bookingService.updateBooking(id, {
          proposed_by: userEmail
        })
      );

      await Promise.all(updatePromises);
      alert("Bookings marked successfully.");
      window.location.reload();
    } catch (error) {
      alert("Failed to mark orders. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const DeleteTheOrder = async () => {
    const selectedOrderIds = Object.keys(selectedOrders).filter(
      (id) => selectedOrders[id]
    );

    if (selectedOrderIds.length === 0) {
      return;
    }

    try {
      setLoading(true);
      const updatePromises = selectedOrderIds.map(id => 
        bookingService.updateBookingStatus(id, 'deleted')
      );

      await Promise.all(updatePromises);
      alert("Bookings deleted successfully.");
      window.location.reload();
    } catch (error) {
      alert("Failed to delete orders. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const DropdownIndicator = (props) => {
    return (
      components.DropdownIndicator && (
        <components.DropdownIndicator {...props}>
          <div style={{ width: "16px", margin: "0" }}>
            <svg
              version="1.1"
              id="Isolationsmodus"
              x="0px"
              y="0px"
              viewBox="0 0 322.9 257.78"
              shapeRendering="geometricPrecision"
            >
              <path
                xmlns="http://www.w3.org/2000/svg"
                d="M208.27,7.37c-4.69-4.79-11.12-7.4-17.72-7.81c-0.4-0.03-0.8-0.05-1.22-0.05l0,0h-35.34h-70.2H31.14h-4.27  C17.33-0.46,7.87,4.51,3.34,13.12C-1.19,21.65-1.3,32.3,4.19,40.47c0.85,1.3,1.74,2.56,2.6,3.82C17.3,59.73,27.77,75.2,38.27,90.64  c12.47,18.33,24.91,36.7,37.39,55.03c3.08,4.53,6.13,9.02,9.21,13.54c3.97,5.86,9.06,10.5,16.08,12.36  c6.87,1.86,14.85,0.93,20.9-2.93c3.79-2.45,6.24-5.12,8.8-8.79c8.06-11.54,15.82-23.3,23.76-34.96  c13.14-19.33,26.25-38.67,39.39-57.96c4.68-6.87,9.32-13.73,14-20.63c4.31-6.31,8.39-12.13,8.2-20.41  C215.85,18.95,213.1,12.34,208.27,7.37z"
              />
            </svg>
          </div>
        </components.DropdownIndicator>
      )
    );
  };
  
  const handleChange = (event) => {
    setSearchTerm(event.target.value);
  };
  
  const navigate = useNavigate();
  
  const proposeClick = async (event) => {
    try {
      // Mark the booking as being processed by us
      await bookingService.updateBooking(event.id, {
        is_being_processed: 1,
        processing_by: userId
      });
      
      // Navigate to the propose page
      navigate(`/admin/propose?bookingId=${event.id}&group=${event.group || ''}`);
    } catch (error) {
      console.error("Error processing booking:", error);
      alert("Failed to process order. Please try again.");
    }
  };

  // Helper function to format a date with better error handling
  const formatDateWithFallback = (dateString, fallback = 'N/A') => {
    if (!dateString) return fallback;
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return fallback;
      
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (e) {
      console.error(`Error formatting date: ${dateString}`, e);
      return fallback;
    }
  };

  const getFormattedCreationDate = (event) => {
    if (!event) return "N/A";
    
    if (event.created_at) {
      return formatDateWithFallback(event.created_at);
    } else if (event.createdAt) {
      return formatDateWithFallback(event.createdAt);
    } else if (event.creation_date) {
      return formatDateWithFallback(event.creation_date);
    }
    
    return "N/A";
  };

  const safeOptions = (events, propertyName) => {
    if (!events || !Array.isArray(events)) return [];
    
    return [...new Set(events
      .filter(event => event && event[propertyName])
      .map(event => {
        const value = event[propertyName];
        // Handle objects with label property (like client, position)
        if (typeof value === 'object' && value !== null) {
          return value.label || value.name || value.value || JSON.stringify(value);
        }
        // Handle strings and other primitive types
        return value;
      })
      .filter(value => value && value.toString().trim() !== ''))]
      .map(value => ({ value, label: value }));
  };

  const startDateOptions = events && events.length ? 
    [...new Set(events
      .filter(event => event && event.start)
      .map(event => {
        try {
          return new Date(event.start).toLocaleDateString();
        } catch (e) {
          return null;
        }
      })
      .filter(date => date !== null))]
      .map(date => ({ value: date, label: date })) : [];

  // Helper function to get location short for filters
  const getLocationShort = (event) => {
    if (!event) return '';
    
    // Handle location as an object with short property
    if (event.location && typeof event.location === 'object') {
      return event.location.short || '';
    }
    
    // Handle direct location_short property
    if (event.location_short) {
      return event.location_short;
    }
    
    return '';
  };

  // Create user options from multiple user fields in events
  const getUsersFromEvents = () => {
    const users = new Set();
    if (events && Array.isArray(events)) {
      events.forEach(event => {
        if (event.created_by) users.add(event.created_by);
        if (event.proposed_by) users.add(event.proposed_by);
        if (event.proposedBy) users.add(event.proposedBy);
        if (event.processing_by) users.add(event.processing_by);
      });
    }
    return Array.from(users).filter(user => user && user.trim() !== '')
      .map(user => ({ value: user, label: user }));
  };

  const clientOptions = safeOptions(events, 'client');
  const positionOptions = safeOptions(events, 'position');
  const setOptions = safeOptions(events, 'set');
  const locationOptions = events && Array.isArray(events) ? 
    [...new Set(events.map(event => getLocationShort(event)).filter(location => location !== ''))]
      .map(location => ({ value: location, label: location })) : [];
  const userOptions = getUsersFromEvents();

  const filteredEvents = events && Array.isArray(events) ? events
    .filter((event) => {
      if (!event) return false;
      
      // Only show events with "new order" status
      if (event.status !== 'new order') return false;
      
      const matchesSearchTerm = !searchTerm || (
        (event.boktitle && event.boktitle.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (event.set && event.set.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (event.talent && event.talent.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (event.status && event.status.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (event.company && event.company.toLowerCase().includes(searchTerm.toLowerCase()))
      );

      let startDateString = "";
      try {
        startDateString = event.start ? new Date(event.start).toLocaleDateString() : "";
      } catch (e) {
        startDateString = "";
      }

      const matchesStartDate = !selectedstartDate || startDateString === selectedstartDate.value;
      const matchesClient = !selectedClient || (() => {
        const eventClient = event.client;
        if (typeof eventClient === 'object' && eventClient !== null) {
          return (eventClient.label || eventClient.name || eventClient.value) === selectedClient.value;
        }
        return eventClient === selectedClient.value;
      })();
      
      const matchesPosition = !selectedPosition || (() => {
        const eventPosition = event.position;
        if (typeof eventPosition === 'object' && eventPosition !== null) {
          return (eventPosition.label || eventPosition.name || eventPosition.value) === selectedPosition.value;
        }
        return eventPosition === selectedPosition.value;
      })();
      const matchesSet = !selectedSet || event.set === selectedSet.value;
      const matchesLocation = !selectedLocation || selectedLocation.length === 0 || 
        (Array.isArray(selectedLocation) 
          ? selectedLocation.some(loc => getLocationShort(event) === loc.value)
          : getLocationShort(event) === selectedLocation.value);
      const matchesUser = !selectedUser || (
        event.created_by === selectedUser.value ||
        event.proposed_by === selectedUser.value ||
        event.proposedBy === selectedUser.value ||
        event.processing_by === selectedUser.value
      );

      return (
        matchesSearchTerm &&
        matchesClient &&
        matchesPosition &&
        matchesStartDate &&
        matchesSet &&
        matchesLocation &&
        matchesUser
      );
    }) : [];

  const sortedAndFilteredEvents = [...filteredEvents].sort((a, b) => {
    // Get the effective sort date for each item
    const getEffectiveDate = (event) => {
      if (!event.group || event.group === 'No Group') {
        // For non-grouped items, use their individual start date
        return new Date(event.start || 0).getTime();
      } else {
        // For grouped items, use the earliest start date in their group
        const groupEvents = filteredEvents.filter(e => e.group === event.group);
        return Math.min(...groupEvents.map(e => new Date(e.start || 0).getTime()));
      }
    };
    
    const effectiveDateA = getEffectiveDate(a);
    const effectiveDateB = getEffectiveDate(b);
    
    // First sort by effective date (chronological order)
    if (effectiveDateA !== effectiveDateB) {
      return effectiveDateA - effectiveDateB;
    }
    
    // If effective dates are the same, handle grouping
    const groupA = a.group || 'No Group';
    const groupB = b.group || 'No Group';
    
    if (groupA !== groupB) {
      // Different groups with same effective date
      if (groupA === 'No Group' && groupB !== 'No Group') {
        // Single order comes before group when dates are equal
        return -1;
      } else if (groupA !== 'No Group' && groupB === 'No Group') {
        // Group comes after single order when dates are equal
        return 1;
      } else {
        // Both have groups, use group name as tiebreaker
        return groupA.localeCompare(groupB);
      }
    }
    
    // Same group (or both non-grouped), sort by individual start date
    const startDateA = new Date(a.start || 0);
    const startDateB = new Date(b.start || 0);
    return startDateA - startDateB;
  });

  // Improved helper to get location information from the event
  const getLocationInfo = (event) => {
    let locationName = '';
    let locationCity = '';
    let locationShort = '';
    
    // console.log("Getting location info for event:", { 
      // location: event.location,
      // location_name: event.location_name,
      // location_city: event.location_city,
      // location_short: event.location_short
    // });
    
    // Handle location as an object with name/city/short properties
    if (event.location && typeof event.location === 'object') {
      locationName = event.location.name || '';
      locationCity = event.location.city || '';
      locationShort = event.location.short || '';
      // console.log("Found location object:", { locationName, locationCity, locationShort });
    }
    
    // Handle direct location properties
    locationName = event.location_name || locationName || (typeof event.location === 'string' ? event.location : '');
    locationCity = event.location_city || locationCity;
    locationShort = event.location_short || locationShort;
    
    // console.log("Final location info:", { locationName, locationCity, locationShort });
    return { locationName, locationCity, locationShort };
  };

  // Helper to get the client display value, handling both string and object formats
  const getClientDisplay = (event) => {
    if (!event) return '';
    
    // Log the client data to debug
    // console.log("Client data:", event.client);
    
    if (event.client) {
      // Handle client as an object with label property
      if (typeof event.client === 'object' && event.client.label) {
        return event.client.label;
      }
      
      // Handle client as a string
      if (typeof event.client === 'string') {
        return event.client;
      }
    }
    
    return '';
  };

  // Helper to get the position display value, handling both string and object formats
  const getPositionDisplay = (event) => {
    if (!event) return '';
    
    // Log the position data to debug
    // console.log("Position data:", event.position);
    
    if (event.position) {
      // Handle position as an object with label property
      if (typeof event.position === 'object' && event.position.label) {
        return event.position.label;
      }
      
      // Handle position as a string
      if (typeof event.position === 'string') {
        return event.position;
      }
    }
    
    return '';
  };

  // Helper function to determine if a booking is early or late shift
  const getShiftType = (event) => {
    const startTime = event.start_time || event.startTime || '';
    
    if (!startTime) return 'early'; // Default to early if no time specified
    
    try {
      // Parse the time string (expecting format like "09:00", "12:30", etc.)
      const timeParts = startTime.split(':');
      if (timeParts.length >= 2) {
        const hours = parseInt(timeParts[0], 10);
        const minutes = parseInt(timeParts[1], 10);
        
        // Convert to total minutes for easier comparison
        const totalMinutes = hours * 60 + minutes;
        const noonInMinutes = 12 * 60; // 12:00 = 720 minutes
        
        return totalMinutes >= noonInMinutes ? 'late' : 'early';
      }
    } catch (error) {
      console.error('Error parsing start time:', startTime, error);
    }
    
    return 'early'; // Default to early if parsing fails
  };

  // Helper function to determine if a color is dark or light
  const isColorDark = (hexColor) => {
    if (!hexColor) return false;
    
    // Remove # if present
    const hex = hexColor.replace('#', '');
    
    // Convert to RGB
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Calculate luminance using the formula for relative luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Return true if dark (luminance < 0.5)
    return luminance < 0.5;
  };

  // Component to display user email for processing user
  const ProcessingUserDisplay = ({ processingUserId }) => {
    const [displayEmail, setDisplayEmail] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      const fetchEmail = async () => {
        if (!processingUserId) {
          setLoading(false);
          return;
        }

        // If it's already an email, use it directly without API call
        if (processingUserId.includes('@')) {
          setDisplayEmail(processingUserId);
          setLoading(false);
          return;
        }

        try {
          const email = await getUserEmail(processingUserId);
          setDisplayEmail(email);
        } catch (error) {
          console.error('Error fetching email:', error);
        } finally {
          setLoading(false);
        }
      };

      fetchEmail();
    }, [processingUserId]);

    if (loading) {
      return <div className="processing-user"></div>;
    }

    return (
      <div className="processing-user" title={`Processing by: ${displayEmail}`}>
        {displayEmail}
      </div>
    );
  };

  return (
    <div>
      <div></div>
      
      <div style={{ marginTop: '40px', marginBottom: '60px' }}>
        <input
          type="text"
          placeholder="Search"
          value={searchTerm}
          onChange={handleChange}
          className='inputtext search'
        />
        
        <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '10px' }}>
            <Select
              value={selectedClient}
              onChange={setSelectedClient}
              options={clientOptions}
              isClearable
              placeholder="Filter by Client"
              styles={customStyles}
            />
            <Select
              value={selectedPosition}
              onChange={setSelectedPosition}
              options={positionOptions}
              isClearable
              placeholder="Filter by Position"
              styles={customStyles}
            />
            <Select
              value={selectedSet}
              onChange={setSelectedSet}
              options={setOptions}
              isClearable
              placeholder="Filter by Set"
              styles={customStyles}
            />
            <Select
              value={selectedLocation}
              onChange={setSelectedLocation}
              options={locationOptions}
              isMulti
              isClearable
              placeholder="Filter by Location"
              styles={customStyles}
            />
            <Select
              value={selectedUser}
              onChange={setSelectedUser}
              options={userOptions}
              isClearable
              placeholder="Filter by User"
              styles={customStyles}
            />
          </div>
          
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', height: '100%' }}>
            <Select
              value={selectedPreferenceIndex !== null ? preferenceOptions.find(option => option.value === selectedPreferenceIndex) : null}
              onChange={handlePreferenceChange}
              options={preferenceOptions}
              isClearable
              placeholder="Load Preference"
              styles={customStyles}
            />
            <button className="button" onClick={saveOrUpdateFilterPreference} disabled={loading} style={{minHeight:'50px', padding:'6px 24px'}}>
              {selectedPreferenceIndex !== null ? "Update Preference" : "Save as Preference"}
            </button>
          </div>
        </div>
        
        <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
          <button className="button" onClick={markTheOrder} disabled={loading || Object.values(selectedOrders).filter(Boolean).length === 0}>
            Mark Selected
          </button>
          <button className="button" onClick={DeleteTheOrder} disabled={loading || Object.values(selectedOrders).filter(Boolean).length === 0}>
            Delete Selected
          </button>
        </div>
      </div>

      <div className='content-wrapper content-wrapper-talent-list'>
        <div className='list-tasks-header'>
          <div style={{ flexBasis: '40px' }}></div>
          <div style={{ flexBasis: '120px' }}>Group / Processing</div>
          <div style={{ flexBasis: '120px' }}>Start/End</div>
          <div style={{ flexBasis: '150px' }}>Client</div>
          <div style={{ flexBasis: '150px' }}>Studio Location</div>
          <div style={{ flexBasis: '100px' }}>Set</div>
          <div style={{ flexBasis: '60px' }}>KPI</div>
          <div style={{ flexBasis: '120px' }}>Position</div>
          <div style={{ flexBasis: '120px' }}>Model</div>
          <div style={{ flexBasis: '130px' }}>Created</div>
          <div style={{ flexBasis: '120px' }}>S. Info / Kind Info</div>
          <div style={{ flexBasis: '80px' }}></div>
        </div>
      </div>

      {sortedAndFilteredEvents.map((event, index) => {
        // console.log("Processing event in map:", event);
        
        // Format start/end dates
        const formattedStartDate = formatDateWithFallback(event.start);
        const formattedEndDate = formatDateWithFallback(event.end || event.start);
        
        // Format created_at date and get created_by
        const dateAdded = formatDateWithFallback(event.created_at);
        const createdBy = event.created_by || '';
        
        // console.log("Created fields:", { created_at: event.created_at, created_by: event.created_by, dateAdded, createdBy });
        
        const bookingId = event.bookingId || event.id;
        const processingUser = event.processing_by || '';
        const proposedBy = event.proposed_by || event.proposedBy || '';
        const isBeingProcessed = false; // Removed processing check - anyone can process any booking
        const groupId = event.group || '';
        const { locationName, locationCity, locationShort } = getLocationInfo(event);
        const studio = event.studio || '';
        const kpi = event.kpi || '';
        const sonstigeInfo = event.sonstige_info || event.SonstigeInfo || event.additional_info || '';
        const kindInfo = event.kind_info || event.kindInfo || '';
        const talent = event.talent_name || event.talent || '';

        const positionDisplay = getPositionDisplay(event);
        const clientDisplay = getClientDisplay(event);

        // Extract number of days from the title of KIND / Copywrite orders
        // ("KIND Order - N day(s)" / "Copywrite Order - N day(s)")
        const isTyped = isTypedBooking(event);
        const typeLabel = orderTypeLabel(getOrderType(event));
        const getKindDays = () => {
          if (isTyped && event.title) {
            // Prefix-agnostic: works for both "KIND Order - N days" and "Copywrite Order - N days"
            const match = event.title.match(/(\d+)\s+day/i);
            return match ? `${match[1]} day${match[1] === '1' ? '' : 's'}` : '';
          }
          return '';
        };
        const kindDays = getKindDays();

        // Determine shift type and apply appropriate CSS class
        const shiftType = getShiftType(event);
        const shiftClass = shiftType === 'late' ? 'late-shifts' : 'early-shifts';

        return (
          <div 
            className={`list-tasks-item ${shiftClass}`} 
            key={bookingId || index}
            style={{
              backgroundColor: '#ffffff',
              borderLeft: event.group_color || event.groupColor ? `4px solid ${event.group_color || event.groupColor}` : 'none'
            }}
          >
            <div style={{ flexBasis: '40px', textAlign: 'center' }}>
              <input
                type="checkbox"
                checked={!!selectedOrders[bookingId]}
                onChange={() => handleCheckboxChange(bookingId)}
              />
            </div>
            <div 
              style={{ 
                flexBasis: '120px'
              }}
            >
              <div 
                style={{
                  backgroundColor: event.group_color || event.groupColor || 'transparent',
                  color: isColorDark(event.group_color || event.groupColor) ? '#ffffff' : '#000000',
                  padding: '4px',
                  borderRadius: '4px',
                  fontWeight: 'bold',
                  textAlign: 'center'
                }}
              >
                {groupId}
              </div>
              {processingUser && (
                <ProcessingUserDisplay processingUserId={processingUser} />
              )}
              {proposedBy && proposedBy !== processingUser && (
                <div className="proposed-by" title={`Proposed by: ${proposedBy}`}>
                  {proposedBy}
                </div>
              )}
            </div>
            <div style={{ flexBasis: '120px' }}>
              <div>
                {formattedStartDate}
                {formattedStartDate !== formattedEndDate && (
                  <div>{formattedEndDate}</div>
                )}
                {(event.start_time || event.startTime) && (
                  <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                    {event.start_time || event.startTime} - {event.end_time || event.endTime}
                  </div>
                )}
              </div>
              {event.created_at && (
                <div style={{ 
                  fontSize: '0.75rem', 
                  color: '#666', 
                  marginTop: '2px',
                  fontStyle: 'italic'
                }}>
                  Created: {new Date(event.created_at).toLocaleDateString()} {new Date(event.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                </div>
              )}
            </div>
            <div style={{ flexBasis: '150px' }}>{clientDisplay}</div>
            <div style={{ flexBasis: '150px' }}>
              {locationShort && studio ? (
                <>
                  {locationShort} - {studio}
                  {locationCity && <div className="location-city">{locationCity}</div>}
                </>
              ) : locationShort ? (
                <>
                  {locationShort}
                  {locationCity && <div className="location-city">{locationCity}</div>}
                </>
              ) : studio ? (
                studio
              ) : (
                ''
              )}
            </div>
            <div style={{ flexBasis: '100px' }}>{event.set}</div>
            <div style={{ flexBasis: '60px' }}>
              {(() => {
                const kpiValue = kpi;
                if (kpiValue === null || kpiValue === undefined || kpiValue === '') {
                  return '';
                }
                
                // Convert to number and round to integer
                const numericValue = parseFloat(kpiValue);
                if (!isNaN(numericValue)) {
                  return Math.round(numericValue);
                }
                
                // If it's not a valid number, return the original value
                return kpiValue;
              })()}
            </div>
            <div style={{ flexBasis: '120px' }}>{positionDisplay}</div>
            <div style={{ flexBasis: '120px' }}>
              {isTyped ? (
                <span style={{ fontWeight: '700', color: '#34C759' }}>{kindDays}</span>
              ) : (
                talent
              )}
            </div>
            <div style={{ flexBasis: '130px' }}>
              {dateAdded}
              {createdBy && (
                <div className="created-by" title={`Created by: ${createdBy}`}>
                  {createdBy}
                </div>
              )}
            </div>
            <div style={{ flexBasis: '120px' }}>
              {sonstigeInfo && kindInfo ? (
                <>
                  <div>{sonstigeInfo}</div>
                  <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>{typeLabel}: {kindInfo}</div>
                </>
              ) : kindInfo ? (
                <span style={{ fontSize: '11px', color: '#666' }}>{typeLabel}: {kindInfo}</span>
              ) : (
                sonstigeInfo
              )}
            </div>
            <div style={{ flexBasis: '80px' }}>
              <button 
                className="edit-button"
                onClick={() => proposeClick(event)}
              >
                <img src={proposeTalent} alt="Propose" className="edit-container" />
              </button>
            </div>
          </div>
        );
      })}

      {sortedAndFilteredEvents.length === 0 && (
        <div style={{ textAlign: 'center', margin: '40px 0', color: '#666', fontStyle: 'italic' }}>
          No new orders available
        </div>
      )}

      <style jsx>{`
        .processing-user {
          display: block;
          font-size: 11px;
          font-style: italic;
          margin-top: 4px;
             color: #1976d2;
        }
        
        .proposed-by {
          display: block;
          font-size: 11px;
          margin-top: 2px;
          color: #1976d2;
        }
        
        .location-city {
          display: block;
          font-size: 11px;
          color: #666;
          margin-top: 2px;
        }
        
        .created-by {
          display: block;
          font-size: 11px;
          color: #666;
          margin-top: 2px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        
        .edit-button.disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .list-tasks-item.early-shifts {
          background: #d2f3ff !important;
          flex-direction: row ;
        }
        
        .list-tasks-item.late-shifts {
          background: #ffd9ad !important;
        }
      `}</style>
    </div>
  );
};

export default NewOrdersList;
